class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      // Use a single delegated event listener for all form changes
      // Don't use capture phase - we want the checkbox state to be updated first
      facetForm.addEventListener('change', (event) => {
        const target = event.target;
        
        // Skip stock filter checkboxes (client-side only)
        if (target.classList.contains('stock-filter-checkbox')) {
          return;
        }
        
        // Handle mobile checkboxes - trigger immediately
        if (target.classList.contains('mobile-facets__checkbox')) {
          const form = target.closest('form');
          if (form) {
            const searchParams = this.createSearchParams(form);
            this.onSubmitForm(searchParams, event);
          }
          return;
        }
        
        // Handle desktop checkboxes and other inputs (selects, etc.)
        if (target.type === 'checkbox' || target.tagName === 'SELECT') {
          // Collect all form data from desktop forms
          // The change event fires after the checkbox state is updated, so we can collect immediately
          const allForms = document.querySelectorAll('facet-filters-form form');
          const forms = [];
          
          allForms.forEach((f) => {
            // Include desktop filter and sort forms
            if (f.id === 'FacetSortForm' || f.id === 'FacetFiltersForm' || f.id === 'FacetSortDrawerForm') {
              const noJsElements = f.querySelectorAll('.no-js-list');
              noJsElements.forEach((el) => el.remove());
              const params = this.createSearchParams(f);
              if (params) {
                forms.push(params);
              }
            }
          });
          
          if (forms.length > 0) {
            this.onSubmitForm(forms.join('&'), event);
          } else {
            // Fallback: use the form that contains the checkbox
            const form = target.closest('form');
            if (form) {
              const searchParams = this.createSearchParams(form);
              if (searchParams) {
                this.onSubmitForm(searchParams, event);
              }
            }
          }
        }
      }); // No capture phase - let the event bubble normally
      
      // Also listen for input events for other form elements
      facetForm.addEventListener('input', (event) => {
        const target = event.target;
        // Skip stock filter and mobile checkboxes (handled above)
        if (target.classList.contains('stock-filter-checkbox') || 
            target.classList.contains('mobile-facets__checkbox')) {
          return;
        }
        // Only handle non-checkbox inputs (like price range)
        if (target.type !== 'checkbox') {
          this.debouncedOnSubmit(event);
        }
      });
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    // Ensure searchParams is a string
    if (!searchParams || searchParams.trim() === '') {
      searchParams = '';
    }
    
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const productGridContainer = document.getElementById('ProductGridContainer');
    
    if (productGridContainer) {
      const collection = productGridContainer.querySelector('.collection');
      if (collection) {
        collection.classList.add('loading');
      }
      // Swap the current cards for height-matched shimmer skeletons so the
      // wait reads as "loading these results", not a blank/spinner gap.
      FacetFiltersForm.showGridSkeleton();
    }

    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}${searchParams ? '&' + searchParams : ''}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  // Replace the live product cards with same-count, height-matched shimmer
  // skeletons. Snapshots the outgoing markup so it can be restored if the
  // fetch fails (skeletons must never get stuck on screen).
  static showGridSkeleton() {
    const grid = document.getElementById('product-grid');
    if (!grid) return;
    const items = grid.querySelectorAll('.grid__item');
    if (!items.length) return;
    if (grid.dataset.skeletonActive === 'true') return;

    // Lock the grid to its current height so swapping in skeletons (and later
    // the real results) can never change the page height -> zero layout shift.
    const gridHeight = Math.round(grid.getBoundingClientRect().height);
    const cardHeight = Math.round(items[0].getBoundingClientRect().height);
    const minHeight = cardHeight > 40 ? cardHeight : 320;
    const count = Math.min(items.length, 24);

    FacetFiltersForm._gridSnapshot = grid.innerHTML;
    grid.dataset.skeletonActive = 'true';
    if (gridHeight > 0) {
      grid.style.height = gridHeight + 'px';
      grid.style.overflow = 'hidden';
    }

    const card =
      '<div class="skeleton skeleton-card__media"></div>' +
      '<div class="skeleton skeleton-card__line skeleton-card__line--title"></div>' +
      '<div class="skeleton skeleton-card__line skeleton-card__line--price"></div>';
    let markup = '';
    for (let i = 0; i < count; i++) {
      markup +=
        '<li class="grid__item skeleton-card-item" style="min-height:' +
        minHeight +
        'px" aria-hidden="true">' +
        card +
        '</li>';
    }
    grid.innerHTML = markup;
  }

  static restoreGridFromSkeleton() {
    const grid = document.getElementById('product-grid');
    if (grid && grid.dataset.skeletonActive === 'true' && FacetFiltersForm._gridSnapshot != null) {
      grid.innerHTML = FacetFiltersForm._gridSnapshot;
    }
    if (grid) {
      grid.style.height = '';
      grid.style.overflow = '';
      delete grid.dataset.skeletonActive;
    }
    FacetFiltersForm._gridSnapshot = null;
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      })
      .catch(() => {
        // Network/parse failure: put the original cards back so the shopper
        // is never left staring at frozen skeletons.
        FacetFiltersForm.restoreGridFromSkeleton();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
    
    // Re-apply stock filter after products are loaded via AJAX
    setTimeout(() => {
      // Re-setup checkbox listeners for newly loaded checkboxes
      if (window.stockFilterInstance) {
        window.stockFilterInstance.setupCheckboxListeners();
        
        // Re-apply filter if checkbox is checked
        const stockCheckbox = document.querySelector('.stock-filter-checkbox:checked');
        if (stockCheckbox) {
          window.stockFilterInstance.handleStockFilter(true);
        }
      }
    }, 100);
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    // Close mobile filter drawer after filters are applied
    const mobileMenuDrawer = document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer');
    const mainDetailsToggle = mobileMenuDrawer?.querySelector('.mobile-facets__disclosure');
    
    // Check if drawer is open (either by open attribute or menu-opening class)
    const isDrawerOpen = mainDetailsToggle && (
      mainDetailsToggle.hasAttribute('open') || 
      mainDetailsToggle.classList.contains('menu-opening')
    );
    
    if (isDrawerOpen) {
      
      // Remove open and menu-opening from all details inside menu-drawer
      mobileMenuDrawer?.querySelectorAll('details').forEach(details => {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
      });
      
      // Also remove from the main details toggle
      if (mainDetailsToggle) {
        mainDetailsToggle.removeAttribute('open');
        mainDetailsToggle.classList.remove('menu-opening');
        const mainSummary = mainDetailsToggle.querySelector('summary');
        if (mainSummary) {
          mainSummary.setAttribute('aria-expanded', false);
        }
      }
      
      // Remove submenu-open classes
      mobileMenuDrawer?.querySelectorAll('.submenu-open').forEach((submenu) => {
        submenu.classList.remove('submenu-open');
      });
      
      // Remove overflow-hidden from body - try all possible breakpoint classes
      const breakpoint = mobileMenuDrawer?.dataset?.breakpoint || 'mobile';
      document.body.classList.remove(`overflow-hidden-${breakpoint}`);
      document.body.classList.remove('overflow-hidden-mobile');
      document.body.classList.remove('overflow-hidden');
      
      // Remove trap focus if it exists
      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus();
      }
      
      // Close any open submenus
      mobileMenuDrawer?.querySelectorAll('.mobile-facets__details[open]').forEach((details) => {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
        details.dataset.mobileFacetShouldBeOpen = 'false';
        const summary = details.querySelector('summary');
        if (summary) {
          summary.setAttribute('aria-expanded', false);
        }
      });
      
    }

    // Re-bind events on mobile menu drawer after content is updated
    if (mobileMenuDrawer && typeof mobileMenuDrawer.bindEvents === 'function') {
      // Use a small delay to ensure DOM is updated
      setTimeout(() => {
        mobileMenuDrawer.bindEvents();
      }, 50);
    }
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const target = event.target;
    const isMobileCheckbox = target && target.classList.contains('mobile-facets__checkbox');
    const isStockFilter = target && target.classList.contains('stock-filter-checkbox');
    const form = target ? target.closest('form') : null;
    
    // Don't submit form if stock filter checkbox was clicked
    if (isStockFilter) {
      return; // Stock filter is client-side only, don't submit form
    }
    
    if (isMobileCheckbox && form) {
      // Mobile checkbox - handle immediately
      const searchParams = this.createSearchParams(form);
      this.onSubmitForm(searchParams, event);
    } else {
      // Desktop or other form changes
      const forms = [];
      const isMobile = form && form.id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((f) => {
        if (!isMobile) {
          // Desktop forms - include all filter and sort forms
          if (f.id === 'FacetSortForm' || f.id === 'FacetFiltersForm' || f.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            const params = this.createSearchParams(f);
            if (params) {
              forms.push(params);
            }
          }
        } else if (f.id === 'FacetFiltersFormMobile') {
          // Mobile form
          const params = this.createSearchParams(f);
          if (params) {
            forms.push(params);
          }
        }
      });
      
      // If we have form params, submit them
      if (forms.length > 0) {
        this.onSubmitForm(forms.join('&'), event);
      } else if (form) {
        // Fallback: if we have a form but no params collected, try to submit it directly
        const searchParams = this.createSearchParams(form);
        if (searchParams) {
          this.onSubmitForm(searchParams, event);
        }
      }
    }
  }
  
  // Method to manually trigger filter submission (for Apply Filters button)
  applyFilters() {
    const form = this.querySelector('form');
    if (!form) return;
    
    const searchParams = this.createSearchParams(form);
    this.onSubmitForm(searchParams, null);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (!facetLink) return;
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

// Stock filter functionality
class StockFilter {
  constructor() {
    // Only initialize on collection pages where a product grid exists
    if (!document.getElementById('product-grid')) return;
    this.init();
  }

  init() {
    this.setupCheckboxListeners();

    // Also listen for dynamically added checkboxes (after AJAX loads)
    const formsContainer = document.querySelector('facet-filters-form');
    if (formsContainer && formsContainer instanceof Node) {
      try {
        let facetObsTimer;
        const observer = new MutationObserver(() => {
          clearTimeout(facetObsTimer);
          facetObsTimer = setTimeout(() => this.setupCheckboxListeners(), 200);
        });
        observer.observe(formsContainer, { childList: true, subtree: true });
      } catch (error) {
        // MutationObserver not available
      }
    }

    // Apply stock filter on page load (it's on by default)
    const applyFilter = () => {
      const productGrid = document.getElementById('product-grid');
      if (!productGrid) return;
      this.handleStockFilter(true);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(applyFilter, 300);
      });
    } else {
      setTimeout(applyFilter, 300);
    }
  }

  setupCheckboxListeners() {
    const stockCheckboxes = document.querySelectorAll('.stock-filter-checkbox');
    stockCheckboxes.forEach(checkbox => {
      // Check if listener already attached (simple check by data attribute)
      if (checkbox.dataset.stockFilterListener === 'true') {
        return; // Already has listener
      }
      
      checkbox.dataset.stockFilterListener = 'true';
      
      // Add listener with capture phase to run before other handlers
      const handleChange = (e) => {
        e.stopPropagation(); // Prevent form submission
        e.stopImmediatePropagation(); // Prevent other handlers on same element
        
        const isChecked = e.target.checked;
        
        // Sync all stock filter checkboxes (desktop and mobile)
        document.querySelectorAll('.stock-filter-checkbox').forEach(cb => {
          if (cb !== e.target) {
            cb.checked = isChecked;
          }
        });
        
        // Apply the filter immediately
        this.handleStockFilter(isChecked);
      };
      
      checkbox.addEventListener('change', handleChange, true); // Use capture phase to run first
      
      // Also handle click event to ensure it works on mobile (some mobile browsers need this)
      checkbox.addEventListener('click', (e) => {
        // Don't prevent default - let the checkbox toggle
        // But stop propagation to prevent form submission
        e.stopPropagation();
        
        // On mobile, sometimes change event doesn't fire immediately, so trigger it manually
        setTimeout(() => {
          if (checkbox.checked !== checkbox.dataset.lastChecked) {
            checkbox.dataset.lastChecked = checkbox.checked;
            handleChange({ target: checkbox, stopPropagation: () => {}, stopImmediatePropagation: () => {} });
          }
        }, 10);
      }, true);
    });
  }

  handleStockFilter(showOnlyInStock) {
    const productItems = document.querySelectorAll('#product-grid li.grid__item');
    // Read layout-triggering properties BEFORE any DOM mutations to avoid forced reflow
    const isMobile = window.innerWidth < 750;
    const productGrid = document.getElementById('product-grid');
    let visibleCount = 0;

    if (!showOnlyInStock) {
      // If filter is off, show all products (except deferred ones)
      productItems.forEach((item) => {
        const isDeferredHidden = item.classList.contains('deferred-product') && 
                                 item.hasAttribute('data-deferred') && 
                                 item.getAttribute('data-deferred') === 'true' &&
                                 !item.querySelector('.card-wrapper');
        
        if (!isDeferredHidden) {
          item.style.display = '';
          item.classList.remove('stock-filtered-out');
          visibleCount++;
        }
      });
      this.updateProductCount(visibleCount);
      return;
    }

    // Filter is on - hide out of stock products
    productItems.forEach((item) => {
      // Skip if item is deferred and not yet loaded
      if (item.hasAttribute('data-deferred') && item.getAttribute('data-deferred') === 'true' && 
          item.style.display === 'none') {
        return;
      }
      
      const hasStockAttr = item.getAttribute('data-has-stock');
      // If data-has-stock is not set, check if product card exists (means it's loaded)
      // For loaded products without the attribute, we need to check the product data
      let hasStock = true; // Default to true if we can't determine
      
      if (hasStockAttr !== null) {
        hasStock = hasStockAttr === 'true';
      } else {
        // Try to determine from product card - if card exists, check for availability indicators
        const productCard = item.querySelector('.card-wrapper, .product-card-wrapper');
        if (productCard) {
          // Check if there's a sold-out indicator or unavailable class
          const isSoldOut = productCard.querySelector('.sold-out, .unavailable, [data-sold-out="true"]');
          hasStock = !isSoldOut;
        }
      }
      
      if (!hasStock) {
        item.style.display = 'none';
        item.classList.add('stock-filtered-out');
      } else {
        // Only show if not already hidden by deferred loading
        const isDeferredHidden = item.classList.contains('deferred-product') && 
                                 item.hasAttribute('data-deferred') && 
                                 item.getAttribute('data-deferred') === 'true' &&
                                 !item.querySelector('.card-wrapper');
        
        if (!isDeferredHidden) {
          item.style.display = '';
          item.classList.remove('stock-filtered-out');
          visibleCount++;
        }
      }
    });

    this.updateProductCount(visibleCount);

    // After filtering, ensure we have enough visible products
    if (productGrid && showOnlyInStock) {
      // Read threshold from data attribute (kebab-case to camelCase conversion)
      const thresholdAttr = isMobile ? 'mobile-threshold' : 'desktop-threshold';
      const thresholdValue = productGrid.getAttribute(`data-${thresholdAttr}`);
      const threshold = thresholdValue ? parseInt(thresholdValue, 10) : (isMobile ? 4 : 8);
      
      const inStockCount = parseInt(productGrid.dataset.inStockCount) || 0;
      const totalChecked = parseInt(productGrid.dataset.totalChecked) || 0;
      
      // If we have fewer visible products than the threshold, load more deferred products
      if (visibleCount < threshold) {
        const needed = threshold - visibleCount;
        this.loadMoreDeferredProducts(needed, productGrid);
      }
    }
  }

  async loadMoreDeferredProducts(neededCount, productGrid) {
    // Get all deferred products that haven't been loaded yet
    const deferredProducts = Array.from(
      productGrid.querySelectorAll('li.grid__item.deferred-product[data-deferred="true"]')
    ).filter(item => {
      // Only include products that are still hidden and not yet loaded
      return item.style.display === 'none' && !item.querySelector('.card-wrapper');
    });
    
    if (deferredProducts.length === 0) {
      // Note: If we need more products, they should be loaded via the "Show More" button
      // or by the main product grid loading mechanism. The stock filter only filters
      // products that are already loaded or deferred.
      return;
    }
    
    // Get section settings from the grid
    const sectionId = productGrid.dataset.id;
    if (!sectionId) return;
    
    // Get section settings from the page
    let sectionSettings = {};
    try {
      const sectionSettingsEl = productGrid.closest('[data-section-settings]');
      if (sectionSettingsEl) {
        sectionSettings = JSON.parse(sectionSettingsEl.dataset.sectionSettings || '{}');
      }
    } catch (e) {
      // Could not parse section settings
    }
    
    let loadedCount = 0;
    const productsToLoad = [];
    
    // Collect products to load (prioritize in-stock products)
    for (const item of deferredProducts) {
      if (loadedCount >= neededCount * 2) break; // Load extra to account for out-of-stock items
      
      const hasStock = item.getAttribute('data-has-stock') === 'true';
      const productHandle = item.getAttribute('data-product-handle');
      
      if (productHandle) {
        productsToLoad.push({ item, hasStock, handle: productHandle });
        if (hasStock) {
          loadedCount++;
        }
      }
    }
    
    // Sort: in-stock products first
    productsToLoad.sort((a, b) => {
      if (a.hasStock && !b.hasStock) return -1;
      if (!a.hasStock && b.hasStock) return 1;
      return 0;
    });
    
    // Load products until we have enough visible in-stock products
    let visibleAfterLoad = this.countVisibleProducts(productGrid);
    const maxToLoad = Math.min(productsToLoad.length, neededCount * 3); // Load up to 3x to account for out-of-stock
    
    for (let i = 0; i < maxToLoad && visibleAfterLoad < neededCount; i++) {
      const { item, handle, hasStock } = productsToLoad[i];

      try {
        const sectionUrl1 = `${window.location.pathname}?section_id=card-product-ajax&product=${handle}`;
        const sectionUrl2 = `${window.location.pathname}?sections=card-product-ajax&product=${handle}`;
        
        let response = await fetch(sectionUrl1, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        
        if (!response.ok) {
          response = await fetch(sectionUrl2, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          });
        }
        
        if (!response.ok) continue;
        
        const data = await response.json();
        // Try both possible response keys
        let cardHtml = data['card-product-ajax'] || data[Object.keys(data)[0]];
        
        if (!cardHtml) continue;
        
        // Extract inner content if wrapped in section/div tags
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = cardHtml;
        
        // Try to find the actual card content (might be wrapped)
        const cardContent = tempDiv.querySelector('.card-wrapper, .product-card-wrapper, .card') || tempDiv.firstElementChild || tempDiv;
        const finalHtml = cardContent.outerHTML || cardHtml;
        
        // Replace the placeholder with the actual card
        const placeholder = item.querySelector('.deferred-placeholder');
        if (placeholder) {
          placeholder.outerHTML = finalHtml;
        } else {
          item.innerHTML = finalHtml;
        }
        
        // Remove deferred attributes
        item.removeAttribute('data-deferred');
        item.classList.remove('deferred-product');
        
        // Show the item if it's in stock
        if (hasStock) {
          item.style.display = '';
          visibleAfterLoad++;
        } else {
          item.style.display = 'none';
          item.classList.add('stock-filtered-out');
        }
      } catch (error) {
        // Failed to load product
      }
    }
    
    // Update product count after loading
    const finalVisibleCount = this.countVisibleProducts(productGrid);
    this.updateProductCount(finalVisibleCount);
  }
  
  countVisibleProducts(productGrid) {
    return Array.from(productGrid.querySelectorAll('li.grid__item')).filter(item => {
      return item.style.display !== 'none' && 
             !item.classList.contains('stock-filtered-out') &&
             (!item.hasAttribute('data-deferred') || item.getAttribute('data-deferred') !== 'true' || item.querySelector('.card-wrapper'));
    }).length;
  }

  updateProductCount(count) {
    const countElements = document.querySelectorAll('#ProductCount, #ProductCountDesktop');
    countElements.forEach((el) => {
      if (el) {
        const text = el.textContent || '';
        // Try to preserve the original count format, just update the number
        const match = text.match(/(\d+)/);
        if (match) {
          el.textContent = text.replace(/\d+/, count);
        }
      }
    });
  }
}

// Initialize stock filter when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.stockFilterInstance = new StockFilter();
  });
} else {
  window.stockFilterInstance = new StockFilter();
}
